import React from "react";
import { Link } from "react-router-dom";
import { Bibliography } from "../../types/bibliography";
import { Person } from "../../types/person";
import { Version } from "../../types/version";
import { Work } from "../../types/work";
import { Text } from "../../types/text";
import { Team } from "../../types/team";
import { Colophon } from "../../types/colophon";
import { Place } from "../../types/place";
import { UserEnum } from "../../types/userEnum";
import { SystemEntityCollections } from "../../shared/enums/shared-enums";
import { RoutesLogic } from "../../common/entities/routesLogic";

interface InternalEntityLinkProps {
  _key?: string;
  system_language_type?: string;
  entityCollection: SystemEntityCollections;
}

export type EntityReferenceLinkMode = "edit" | "view";

interface InternalEntityLinkComponentProps extends InternalEntityLinkProps {
  children: React.ReactNode;
  className?: string;
  linkMode?: EntityReferenceLinkMode;
}

export const extractEditLink = ({
  _key,
  system_language_type,
  entityCollection,
}: InternalEntityLinkProps): string => {
  return `/${RoutesLogic.getEditRoute(entityCollection, system_language_type, _key)}`;
};

export const extractPersonEditLink = (person: Person) =>
  extractEditLink({
    ...person,
    entityCollection: SystemEntityCollections.PERSON,
  });
export const extractVersionEditLink = (version: Version) =>
  extractEditLink({
    ...version,
    entityCollection: SystemEntityCollections.VERSION,
  });
export const extractVersionViewLink = (version: Version) =>
  extractViewLink({
    ...version,
    entityCollection: SystemEntityCollections.VERSION,
  });
export const extractTextEditLink = (text: Text) =>
  extractEditLink({ ...text, entityCollection: SystemEntityCollections.TEXT });
export const extractWorkEditLink = (work: Work) =>
  extractEditLink({ ...work, entityCollection: SystemEntityCollections.WORK });
export const extractWorkViewLink = (work: Work) =>
  extractViewLink({ ...work, entityCollection: SystemEntityCollections.WORK });
export const extractTeamEditLink = (team: Team) =>
  extractEditLink({ ...team, entityCollection: SystemEntityCollections.TEAM });
export const extractTeamViewLink = (team: Team) =>
  extractViewLink({ ...team, entityCollection: SystemEntityCollections.TEAM });
export const extractColophonEditLink = (colophon: Colophon) =>
  extractEditLink({
    ...colophon,
    entityCollection: SystemEntityCollections.COLOPHON,
  });
export const extractColophonViewLink = (colophon: Colophon) =>
  extractViewLink({
    ...colophon,
    entityCollection: SystemEntityCollections.COLOPHON,
  });
export const extractBibliographyEditLink = (bibliography: Bibliography) =>
  extractEditLink({
    ...bibliography,
    entityCollection: SystemEntityCollections.BIBLIOGRAPHY,
  });
export const extractPlaceEditLink = (place: Place) =>
  extractEditLink({
    ...place,
    entityCollection: SystemEntityCollections.PLACE,
  });

export const extractUserEnumEditLink = (userEnum: UserEnum) =>
  extractEditLink({
    ...userEnum,
    entityCollection: SystemEntityCollections.USER_ENUM,
  });

export const extractViewLink = ({
  _key,
  system_language_type,
  entityCollection,
}: InternalEntityLinkProps): string => {
  return `/${RoutesLogic.getViewRoute(entityCollection, system_language_type, _key)}`;
};

export const collectionHasViewRoute = (
  entityCollection: SystemEntityCollections
): boolean =>
  [
    SystemEntityCollections.PERSON,
    SystemEntityCollections.PLACE,
    SystemEntityCollections.WORK,
    SystemEntityCollections.TEAM,
    SystemEntityCollections.BIBLIOGRAPHY,
    SystemEntityCollections.COLOPHON,
    SystemEntityCollections.VERSION,
  ].includes(entityCollection);

export const extractBibliographyViewLink = (bibliography: Bibliography) =>
  extractViewLink({
    ...bibliography,
    entityCollection: SystemEntityCollections.BIBLIOGRAPHY,
  });

export const extractPersonViewLink = (person: Person) =>
  extractViewLink({
    ...person,
    entityCollection: SystemEntityCollections.PERSON,
  });

export const extractPlaceViewLink = (place: Place) =>
  extractViewLink({
    ...place,
    entityCollection: SystemEntityCollections.PLACE,
  });

const InternalEntityLink: React.FC<InternalEntityLinkComponentProps> = ({
  children,
  className,
  linkMode = "edit",
  ...props
}) => {
  if (linkMode === "view" && !collectionHasViewRoute(props.entityCollection)) {
    return <>{children}</>;
  }

  const to =
    linkMode === "view" ? extractViewLink(props) : extractEditLink(props);

  return (
    <Link to={to} className={className}>
      {children}
    </Link>
  );
};

export default InternalEntityLink;
